import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout.tsx';
import { advertisementAPI } from '../services/api.ts';
import { useDropzone } from 'react-dropzone';
import { uploadToCloudinary } from '../services/index.tsx';
import {
  MegaphoneIcon,
  XMarkIcon,
  PhotoIcon,
  ClockIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';

// ----------------------
// Modal Component
// ----------------------
type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
};

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// ----------------------
// Add Advertisement Modal
// ----------------------
type AddAdvertisementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
};

const AddAdvertisementModal: React.FC<AddAdvertisementModalProps> = ({
  isOpen,
  onClose,
  onAdded,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    multiple: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select an image file.');
      return;
    }

    if (!time) {
      setError('Please enter a time or schedule.');
      return;
    }

    const uploadImage = uploadToCloudinary(file, 'advertisement');
    const imageUrl = await uploadImage;
    if (!imageUrl) {
      setError('Failed to upload image.');
      return;
    }

    const formData = {
      url: imageUrl,
      time,
      type: 'image',
    };

    try {
      setLoading(true);
      await advertisementAPI.create(formData);
      onAdded();
      onClose();
      setFile(null);
      setTime('');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to add advertisement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Advertisement">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 bg-gray-50 dark:bg-gray-700'
          }`}
        >
          <input {...getInputProps()} />
          <CloudArrowUpIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
          {isDragActive ? (
            <p className="text-primary-600 dark:text-primary-400 font-medium">
              Drop the image here...
            </p>
          ) : (
            <div>
              <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
                {file ? file.name : 'Drag & drop an image here'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                or click to select one
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <ClockIcon className="h-4 w-4" />
            Time / Schedule
          </label>
          <input
            type="text"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
            placeholder="e.g., 30 seconds, 1 minute"
          />
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-600 dark:text-red-200 text-sm font-medium">
              {error}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Adding...' : 'Add Advertisement'}
        </button>
      </form>
    </Modal>
  );
};

// ----------------------
// Update Advertisement Modal
// ----------------------
type UpdateAdvertisementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  advertisement: any;
  onUpdated: () => void;
  onDeleted: () => void;
};

const UpdateAdvertisementModal: React.FC<UpdateAdvertisementModalProps> = ({
  isOpen,
  onClose,
  advertisement,
  onUpdated,
  onDeleted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [time, setTime] = useState(advertisement?.time || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (advertisement) {
      setTime(advertisement.time || '');
    }
  }, [advertisement]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    multiple: false,
  });

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!time) {
      setError('Please enter a time or schedule.');
      return;
    }

    let imageUrl = advertisement.url;
    if (file) {
      const uploadImage = uploadToCloudinary(file, 'advertisement');
      imageUrl = await uploadImage;
      if (!imageUrl) {
        setError('Failed to upload image.');
        return;
      }
    }
    const formData = {
      url: imageUrl,
      time,
      type: 'image',
    };

    try {
      setLoading(true);
      await advertisementAPI.update(advertisement._id, formData);
      onUpdated();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update advertisement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!advertisement) return;
    if (!window.confirm('Are you sure you want to delete this advertisement?'))
      return;

    try {
      setLoading(true);
      await advertisementAPI.delete(advertisement._id);
      onDeleted();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete advertisement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Advertisement">
      <div className="space-y-5">
        {/* Current Image Preview */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Current Advertisement
          </p>
          {advertisement.type === 'image' ? (
            <img
              src={advertisement.url}
              alt="advertisement"
              className="w-[90%] h-48 object-contain rounded-lg"
            />
          ) : (
            <video
              src={advertisement.url}
              controls
              className="w-full h-48 object-contain rounded-lg"
            />
          )}
        </div>

        <form onSubmit={handleUpdate} className="space-y-5">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
              isDragActive
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 bg-gray-50 dark:bg-gray-700'
            }`}
          >
            <input {...getInputProps()} />
            <CloudArrowUpIcon className="h-10 w-10 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
            {isDragActive ? (
              <p className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                Drop the image here...
              </p>
            ) : (
              <div>
                <p className="text-gray-700 dark:text-gray-300 font-medium text-sm mb-1">
                  {file ? file.name : 'Upload new image (optional)'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Drag & drop or click to select
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <ClockIcon className="h-4 w-4" />
              Time / Schedule
            </label>
            <input
              type="text"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
              placeholder="e.g., 30 seconds, 1 minute"
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-red-600 dark:text-red-200 text-sm font-medium">
                {error}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update'}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="flex items-center gap-2 bg-red-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrashIcon className="h-5 w-5" />
              Delete
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

// ----------------------
// Main Advertisement Page Component
// ----------------------
const Advertisement: React.FC = () => {
  const [advertisements, setAdvertisements] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [error, setError] = useState('');
  const [company_id, setCompanyId] = useState<number | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await advertisementAPI.getAll();
      setAdvertisements(response.data as never as any[]);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch advertisements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const handleAdAdded = () => {
    fetchAdvertisements();
  };

  const handleAdUpdated = () => {
    fetchAdvertisements();
  };

  const handleAdDeleted = () => {
    fetchAdvertisements();
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedCompanyId = storedUser ? JSON.parse(storedUser) : null;
    if (storedCompanyId?.company_id) {
      setCompanyId(parseInt(storedCompanyId?.company_id));
    }
    if (storedCompanyId?.role) {
      setRole(storedCompanyId?.role);
    }
  }, []);

  return (
    <Layout>
      <div className="py-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                  <MegaphoneIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    Advertisements
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    Manage your advertisement campaigns
                  </p>
                </div>
              </div>

              {/* Add Button */}
              {!advertisements.find((ad) => ad.company?.id === company_id) &&
                role === 'admin' && (
                  <button
                    onClick={() => setAddModalOpen(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add Advertisement
                  </button>
                )}
            </div>

            {/* Warning Message */}
            {advertisements.find((ad) => ad.company?.id === company_id) && (
              <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                <p className="text-amber-800 dark:text-amber-200 font-medium text-sm">
                  ⚠️ You already have an advertisement. Please update it instead
                  of creating a new one.
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <p className="text-red-600 dark:text-red-200 font-medium text-sm">
                  {error}
                </p>
              </div>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400 font-medium">
                  Loading advertisements...
                </p>
              </div>
            </div>
          ) : advertisements.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
              <PhotoIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No advertisements yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Get started by creating your first advertisement
              </p>
              {role === 'admin' && (
                <button
                  onClick={() => setAddModalOpen(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg"
                >
                  <PlusIcon className="h-5 w-5" />
                  Add Advertisement
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {advertisements.map((ad) => (
                <div
                  key={ad._id}
                  className="group bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Image/Video */}
                  <div className="relative bg-gray-50 dark:bg-gray-700 h-48 overflow-hidden">
                    {ad.type === 'image' ? (
                      <img
                        src={ad.url}
                        alt="advertisement"
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <video
                        src={ad.url}
                        controls
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <ClockIcon className="h-4 w-4" />
                      <span className="font-medium">{ad.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <BuildingOfficeIcon className="h-4 w-4" />
                      <span>{ad.company?.name || 'N/A'}</span>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedAd(ad);
                        setUpdateModalOpen(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AddAdvertisementModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdded={handleAdAdded}
      />
      {selectedAd && (
        <UpdateAdvertisementModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedAd(null);
          }}
          advertisement={selectedAd}
          onUpdated={handleAdUpdated}
          onDeleted={handleAdDeleted}
        />
      )}
    </Layout>
  );
};

export default Advertisement;
